import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Paperclip, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { VALID_STATUSES } from '../../firebase/leads.service';
import { auth } from '../../firebase/firebase.config';
import { sendBroadcast, filterLeadsForBroadcast, validateBroadcast } from '../../firebase/broadcast.service';
import { truncateMessagePreview, parseBroadcastTemplate } from '../../utils/inboxHelpers';
import { filesToValidatedFiles } from '../../utils/attachmentHelpers';
import useAppStore from '../../store/appStore';

// Broadcast variables available for message templates
const BROADCAST_VARIABLES = [
    { key: 'MyName', label: '{{MyName}}' },
    { key: 'FirstName', label: '{{FirstName}}' },
    { key: 'LastName', label: '{{LastName}}' },
    { key: 'FullName', label: '{{FullName}}' },
    { key: 'InterestedProperty', label: '{{Interested Property}}' },
    { key: 'MyPhone', label: '{{MyPhone}}' },
];

const BroadcastModal = ({ onClose, onToastShow }) => {
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedTags, setSelectedTags] = useState([]);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [channel, setChannel] = useState('SMS');
    // attachments are File objects kept in memory until sendBroadcast uploads them
    const [attachments, setAttachments] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendProgress, setSendProgress] = useState({ processed: 0, total: 0 });
    const [sendResult, setSendResult] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);
    const [selectedLeadIds, setSelectedLeadIds] = useState(null); // null = all selected (default)
    const textareaRef = useRef(null);
    const subjectInputRef = useRef(null);
    const fileInputRef = useRef(null);

    // Read leads and user profile from centralized Zustand store
    const storeLeads = useAppStore((s) => s.leads);
    const userProfile = useAppStore((s) => s.userProfile);
    const leads = storeLeads || [];
    const loadingLeads = storeLeads === null;

    const handleInsertVariable = (variable, targetRef, value, setValue) => {
        const ref = targetRef || textareaRef;
        const currentValue = value !== undefined ? value : message;
        const setCurrentValue = setValue || setMessage;
        if (ref.current) {
            const start = ref.current.selectionStart;
            const end = ref.current.selectionEnd;
            const newValue = currentValue.slice(0, start) + variable.label + currentValue.slice(end);
            setCurrentValue(newValue);
            setTimeout(() => {
                ref.current.selectionStart = start + variable.label.length;
                ref.current.selectionEnd = start + variable.label.length;
                ref.current.focus();
            }, 0);
        }
    };

    const handleAttachFile = () => fileInputRef.current?.click();
    const handleFileChange = (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        try {
            const validFiles = filesToValidatedFiles(files);
            setAttachments(prev => [...prev, ...validFiles]);
        } catch (err) {
            console.error('Attachment error:', err);
            setValidationErrors([err.message || 'Attachment error']);
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };
    const handleRemoveFile = (idx) => {
        setAttachments(prev => prev.filter((_, i) => i !== idx));
    };

    // Derive available tags from all leads
    const availableTags = useMemo(() => {
        const tagSet = new Set();
        leads.forEach(lead => {
            (lead.tags || []).forEach(tag => tagSet.add(tag));
        });
        return [...tagSet].sort();
    }, [leads]);

    // Get leads filtered by status & tags (the pool from which checkbox selection applies)
    const filteredPool = useMemo(() => {
        let filtered = leads;

        if (selectedStatus !== 'All') {
            filtered = filtered.filter(lead => lead.status === selectedStatus);
        }

        if (selectedTags.length > 0) {
            filtered = filtered.filter(lead => {
                const leadTags = (lead.tags || []).map(t => t.toLowerCase());
                return selectedTags.every(tag => leadTags.includes(tag.toLowerCase()));
            });
        }

        return filtered;
    }, [leads, selectedStatus, selectedTags]);

    // Reset checkbox selection when filters change (so user sees the new pool)
    useEffect(() => {
        setSelectedLeadIds(null);
    }, [selectedStatus, selectedTags]);

    // Final selected leads: if selectedLeadIds is null, all in pool; otherwise only checked leads
    const getFilteredLeads = () => {
        if (selectedLeadIds === null) return filteredPool;
        return filteredPool.filter(lead => selectedLeadIds.has(lead.id));
    };

    const getFilteredLeadCount = () => getFilteredLeads().length;

    // Checkbox helpers
    const isLeadSelected = (leadId) => selectedLeadIds === null || selectedLeadIds.has(leadId);
    const isAllSelected = selectedLeadIds === null || (filteredPool.length > 0 && filteredPool.every(l => selectedLeadIds.has(l.id)));

    const toggleLeadSelection = (leadId) => {
        setSelectedLeadIds(prev => {
            // If currently "all", switch to explicit set minus the toggled one
            if (prev === null) {
                const allIds = new Set(filteredPool.map(l => l.id));
                allIds.delete(leadId);
                return allIds;
            }
            const next = new Set(prev);
            if (next.has(leadId)) next.delete(leadId);
            else next.add(leadId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedLeadIds(new Set());
        } else {
            setSelectedLeadIds(null); // null = all
        }
    };

    // Handle sending the broadcast
    const handleSendBroadcast = async () => {
        const filteredLeads = getFilteredLeads();

        // Validate before sending. Allow empty message if attachments provided.
        let validation = { valid: true };
        if (message && message.trim()) {
            validation = validateBroadcast(filteredLeads, message, channel);
        } else if (!attachments || attachments.length === 0) {
            validation = validateBroadcast(filteredLeads, message, channel);
        }
        if (!validation.valid) {
            setValidationErrors(validation.errors);
            return;
        }

        setValidationErrors([]);
        setSending(true);
        setSendProgress({ processed: 0, total: filteredLeads.length });

        try {
            const userId = auth.currentUser?.uid;
            if (!userId) throw new Error('Not authenticated');

            // Fetch latest user profile from Firestore to ensure Display Name/Phone properties are up to date
            // This is critical for variables like {{MyName}} and {{MyPhone}} in broadcasts
            // We use state.userProfile if available (fetched on mount), otherwise default to empty strings to avoid Auth fallback violation
            let userData = {
                displayName: userProfile?.displayName || userProfile?.fullName || '',
                fullName: userProfile?.fullName || userProfile?.displayName || '',
                email: auth.currentUser?.email || '',
                phone: userProfile?.phone || '',
            };

            // Double check strict requirement: Firestore is single source of truth.
            // If userProfile failed to load, we effectively send empty strings for MyName/MyPhone rather than stale Auth data.
            // (Except email, which is Auth based for delivery safety, but Auth email is usually synced).

            const result = await sendBroadcast(
                userId,
                filteredLeads,
                message,
                channel,
                userData,
                { subject: (channel === 'EMAIL' && subject) ? subject : undefined, attachments: attachments.length ? attachments : undefined },
                (progress) => setSendProgress(progress)
            );

            setSendResult(result);

            // Show toast if handler provided
            // CRITICAL: Failed messages were NOT saved to Firebase - only successful deliveries are persisted
            if (onToastShow) {
                if (result.failed === 0) {
                    onToastShow({
                        type: 'success',
                        text: `✅ Broadcast delivered to ${result.successful} recipients`,
                    });
                } else if (result.successful > 0) {
                    onToastShow({
                        type: 'warning',
                        text: `⚠️ Delivered to ${result.successful} recipients. ${result.failed} failed to deliver (not saved).`,
                    });
                } else {
                    onToastShow({
                        type: 'error',
                        text: `❌ Broadcast failed - no messages were delivered or saved`,
                    });
                }
            }

            // Close modal after short delay to show result
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (error) {
            console.error('Broadcast error:', error);
            setValidationErrors([error.message || 'Failed to send broadcast']);
            if (onToastShow) {
                onToastShow({
                    type: 'error',
                    text: `❌ Broadcast failed: ${error.message}`,
                });
            }
        } finally {
            setSending(false);
        }
    };

    // Preview mode with expanded recipient list
    if (showPreview) {
        const filteredLeads = getFilteredLeads();
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div onClick={() => setShowPreview(false)} className="absolute inset-0 bg-stone-900/10 backdrop-blur-sm transition-opacity"></div>

                <div className="bg-white w-full max-w-xl rounded-xl shadow-modal border border-notion-border flex flex-col max-h-[90vh] z-10 fade-in">
                    <div className="px-5 py-4 border-b border-notion-border flex items-center justify-between bg-white rounded-t-xl">
                        <div>
                            <h2 className="font-semibold text-sm text-notion-text">Preview Broadcast</h2>
                            <p className="text-xs text-notion-text-light">{filteredLeads.length} recipients via {channel}</p>
                        </div>
                        <button onClick={() => setShowPreview(false)} className="text-stone-400 hover:text-stone-600 transition">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {/* Message Preview */}
                        <div className="bg-notion-sidebar rounded-lg p-4 border border-notion-border">
                            <h3 className="text-xs font-semibold text-notion-text-light uppercase tracking-wide mb-2">Message Preview (First Recipient)</h3>
                            {channel === 'EMAIL' && subject && (
                                <p className="text-sm font-bold text-notion-text mb-2">
                                    {filteredLeads.length > 0
                                        ? parseBroadcastTemplate(subject, filteredLeads[0], userProfile || { displayName: '', phone: '' })
                                        : subject
                                    }
                                </p>
                            )}
                            <p className="text-sm text-notion-text whitespace-pre-wrap">
                                {filteredLeads.length > 0
                                    ? parseBroadcastTemplate(message, filteredLeads[0], userProfile || { displayName: '', phone: '' })
                                    : message
                                }
                            </p>
                        </div>

                        {/* Recipients List */}
                        <div>
                            <h3 className="text-xs font-semibold text-notion-text-light uppercase tracking-wide mb-2">Recipients ({filteredLeads.length})</h3>
                            <div className="bg-white border border-notion-border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                                {filteredLeads.map((lead) => (
                                    <div key={lead.id} className="px-4 py-3 flex items-center justify-between border-b border-notion-border last:border-b-0">
                                        <div className="flex-1 min-w-0">
                                            <span className="text-sm text-notion-text block truncate">
                                                {lead.fullName || lead.name || 'Unnamed Lead'}
                                            </span>
                                            <span className="text-xs text-stone-400 block truncate">
                                                {channel === 'SMS' ? lead.phone : lead.email}
                                            </span>
                                        </div>
                                        {((channel === 'SMS' && !lead.phone) || (channel === 'EMAIL' && !lead.email)) && (
                                            <span className="text-xs text-amber-600 flex items-center gap-1">
                                                <AlertCircle className="w-3 h-3" /> No {channel === 'SMS' ? 'phone' : 'email'}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="px-5 py-4 border-t border-notion-border bg-stone-50/50 rounded-b-xl flex justify-between items-center">
                        <button
                            onClick={() => setShowPreview(false)}
                            className="px-4 py-2 text-xs text-stone-500 hover:text-stone-800 transition"
                        >
                            ← Back to Edit
                        </button>
                        <button
                            onClick={handleSendBroadcast}
                            disabled={sending || filteredLeads.length === 0}
                            className="px-4 py-2 bg-stone-800 hover:bg-stone-700 disabled:bg-stone-400 text-white text-xs font-medium rounded shadow-sm flex items-center gap-2 transition"
                        >
                            {sending ? (
                                <>
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Sending {sendProgress.processed}/{sendProgress.total}...
                                </>
                            ) : (
                                <>Confirm & Send</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Send result view
    if (sendResult) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-stone-900/10 backdrop-blur-sm transition-opacity"></div>

                <div className="bg-white w-full max-w-md rounded-xl shadow-modal border border-notion-border flex flex-col z-10 fade-in p-6 text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h2 className="font-semibold text-lg text-notion-text mb-2">Broadcast Sent!</h2>
                    <p className="text-sm text-notion-text-light mb-4">
                        Successfully sent to {sendResult.successful} recipient{sendResult.successful !== 1 ? 's' : ''}.
                        {sendResult.failed > 0 && (
                            <span className="block text-amber-600 mt-1">
                                {sendResult.failed} failed to send.
                            </span>
                        )}
                    </p>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-stone-800 hover:bg-stone-700 text-white text-sm font-medium rounded shadow-sm mx-auto"
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div onClick={onClose} className="absolute inset-0 bg-stone-900/10 backdrop-blur-sm transition-opacity"></div>

            <div className="bg-white w-full max-w-xl rounded-xl shadow-modal border border-notion-border flex flex-col max-h-[90vh] z-10 fade-in">
                <div className="px-5 py-4 border-b border-notion-border flex items-center justify-between bg-white rounded-t-xl">
                    <div>
                        <h2 className="font-semibold text-sm text-notion-text">New Broadcast</h2>
                        <p className="text-xs text-notion-text-light">Target specific segments.</p>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-600 transition">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto space-y-5 bg-white">
                    <div className="bg-notion-sidebar rounded-lg p-4 border border-notion-border space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-notion-text uppercase tracking-wide">Filters</span>
                            <span className="px-2 py-0.5 bg-stone-800 text-white rounded text-[10px] font-bold">{getFilteredLeadCount()} Selected</span>
                        </div>

                        <div>
                            <div>
                                <label className="block text-[10px] font-medium text-notion-text-light mb-1">Status</label>
                                <select
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    className="w-full text-xs bg-white border border-notion-border rounded px-2 py-2 focus:outline-none"
                                >
                                    <option value="All">All Statuses</option>
                                    {VALID_STATUSES.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Tags Filter */}
                            {availableTags.length > 0 && (
                                <div className="mt-3">
                                    <label className="block text-[10px] font-medium text-notion-text-light mb-1">Tags</label>
                                    {selectedTags.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {selectedTags.map(tag => (
                                                <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone-100 text-stone-600 text-[10px] rounded border border-stone-200">
                                                    {tag}
                                                    <button onClick={() => setSelectedTags(prev => prev.filter(t => t !== tag))} className="text-stone-400 hover:text-stone-700">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <select
                                        value=""
                                        onChange={(e) => {
                                            if (e.target.value && !selectedTags.includes(e.target.value)) {
                                                setSelectedTags(prev => [...prev, e.target.value]);
                                            }
                                        }}
                                        className="w-full text-xs bg-white border border-notion-border rounded px-2 py-2 focus:outline-none"
                                    >
                                        <option value="">Filter by tag...</option>
                                        {availableTags
                                            .filter(t => !selectedTags.includes(t))
                                            .map(tag => (
                                                <option key={tag} value={tag}>{tag}</option>
                                            ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Checkbox Lead Selection */}
                        {filteredPool.length > 0 && (
                            <div className="mt-3 bg-white border border-notion-border rounded-lg overflow-hidden">
                                <div className="px-4 py-2 border-b border-notion-border bg-stone-50 flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={toggleSelectAll}
                                        className="accent-stone-800 w-3.5 h-3.5"
                                    />
                                    <span className="text-xs font-medium text-notion-text">Select All ({filteredPool.length})</span>
                                </div>
                                <div className="max-h-40 overflow-y-auto">
                                    {filteredPool.map((lead) => (
                                        <div key={lead.id} className="px-4 py-2 flex items-center gap-2 border-b border-notion-border last:border-b-0 hover:bg-stone-50 transition">
                                            <input
                                                type="checkbox"
                                                checked={isLeadSelected(lead.id)}
                                                onChange={() => toggleLeadSelection(lead.id)}
                                                className="accent-stone-800 w-3.5 h-3.5 flex-shrink-0"
                                            />
                                            <div className="flex-1 min-w-0 flex items-center justify-between">
                                                <span className="text-xs text-notion-text truncate">
                                                    {lead.fullName || lead.name || 'Unnamed Lead'}
                                                </span>
                                                <span className="text-xs text-stone-400 truncate ml-2">
                                                    {channel === 'SMS' ? lead.phone : lead.email}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="b-chan"
                                        checked={channel === 'SMS'}
                                        onChange={() => setChannel('SMS')}
                                        className="accent-stone-800"
                                    />
                                    <span className={`text-xs font-medium ${channel === 'SMS' ? 'text-notion-text' : 'text-notion-text-light'}`}>SMS</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="b-chan"
                                        checked={channel === 'EMAIL'}
                                        onChange={() => setChannel('EMAIL')}
                                        className="accent-stone-800"
                                    />
                                    <span className={`text-xs font-medium ${channel === 'EMAIL' ? 'text-notion-text' : 'text-notion-text-light'}`}>Email</span>
                                </label>
                            </div>
                            <button
                                onClick={handleAttachFile}
                                className="text-xs flex items-center gap-1 text-stone-500 hover:text-stone-800 transition-colors"
                            >
                                <Paperclip className="w-3 h-3" /> Attach Media
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={handleFileChange}
                                accept=".pdf,image/*,.doc,.docx,.txt"
                                className="hidden"
                            />
                        </div>

                        {attachments.length > 0 && (
                            <div className="mb-2 space-y-1">
                                {attachments.map((a, idx) => (
                                    <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-stone-50 border border-notion-border rounded text-xs">
                                        <Paperclip className="w-3 h-3 text-stone-500" />
                                        <span className="flex-1 text-stone-700 truncate">{a.filename}</span>
                                        <X className="w-4 h-4 cursor-pointer text-stone-400 hover:text-stone-800" onClick={() => handleRemoveFile(idx)} />
                                    </div>
                                ))}
                            </div>
                        )}

                        {channel === 'EMAIL' && (
                            <div className="mb-2">
                                <label className="text-xs text-notion-text-light">Subject</label>
                                <div className="relative border border-notion-border rounded-lg shadow-sm mt-1">
                                    <input
                                        ref={subjectInputRef}
                                        type="text"
                                        placeholder="Enter email subject (supports {{variables}})"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        disabled={sending}
                                        className="w-full px-3 py-2 text-sm rounded-t-lg focus:outline-none disabled:bg-stone-50"
                                    />
                                    <div className="bg-notion-sidebar border-t border-notion-border rounded-b-lg px-2 py-1 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
                                        <span className="text-[10px] text-notion-text-light font-medium mr-0.5">Insert:</span>
                                        {BROADCAST_VARIABLES.map((variable) => (
                                            <button
                                                key={variable.key}
                                                onClick={() => handleInsertVariable(variable, subjectInputRef, subject, setSubject)}
                                                className="px-1.5 py-0.5 bg-white border border-notion-border rounded text-[10px] text-stone-600 hover:bg-stone-50 transition"
                                            >
                                                {variable.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="relative border border-notion-border rounded-lg shadow-sm">
                            <textarea
                                ref={textareaRef}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="w-full p-3 h-32 text-sm bg-white rounded-t-lg focus:outline-none resize-none placeholder-stone-400"
                                placeholder="Hi {{FirstName}}..."
                            ></textarea>

                            <div className="bg-notion-sidebar border-t border-notion-border rounded-b-lg px-2 py-1.5 flex items-center gap-2 overflow-x-auto no-scrollbar">
                                <span className="text-[10px] text-notion-text-light font-medium mr-1">Insert:</span>
                                {BROADCAST_VARIABLES.map((variable) => (
                                    <button
                                        key={variable.key}
                                        onClick={() => handleInsertVariable(variable, textareaRef, message, setMessage)}
                                        className="px-2 py-0.5 bg-white border border-notion-border rounded text-[10px] text-stone-600 hover:bg-stone-50 transition"
                                    >
                                        {variable.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Validation Errors */}
                    {validationErrors.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            {validationErrors.map((error, index) => (
                                <div key={index} className="flex items-center gap-2 text-xs text-red-600">
                                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                                    {error}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="px-5 py-4 border-t border-notion-border bg-stone-50/50 rounded-b-xl flex justify-between items-center">
                    <span className="text-xs text-stone-500">
                        {getFilteredLeadCount() > 0
                            ? `${getFilteredLeadCount()} recipient${getFilteredLeadCount() !== 1 ? 's' : ''} selected`
                            : 'No recipients selected'}
                    </span>
                    <div className="flex gap-3">
                        <button onClick={onClose} className="px-4 py-2 text-xs text-stone-500 hover:text-stone-800 transition">
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                // Preview: allow attachments-only broadcasts
                                let validation = { valid: true };
                                if (message && message.trim()) {
                                    validation = validateBroadcast(getFilteredLeads(), message, channel);
                                } else if (!attachments || attachments.length === 0) {
                                    validation = validateBroadcast(getFilteredLeads(), message, channel);
                                }
                                if (validation.valid) {
                                    setValidationErrors([]);
                                    setShowPreview(true);
                                } else {
                                    setValidationErrors(validation.errors);
                                }
                            }}
                            disabled={getFilteredLeadCount() === 0 || (!message.trim() && attachments.length === 0)}
                            className="px-4 py-2 bg-stone-800 hover:bg-stone-700 disabled:bg-stone-400 disabled:cursor-not-allowed text-white text-xs font-medium rounded shadow-sm flex items-center gap-2 transition"
                        >
                            Preview & Send
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BroadcastModal;